import { Transaction } from 'src/app/interfaces/transaction';
import { TransactionService } from 'src/app/services/payment/transaction.service';
import { AnalyticsService } from 'src/app/services/general/analytics.service';
import { LoginService } from 'src/app/services/general/login.service';
import { UserService } from 'src/app/services/user/user.service';
import { CreditService } from 'src/app/services/subscription/credit.service';
import { LinkService } from 'src/app/services/general/link.service';
import { Router, ActivatedRoute, NavigationExtras, Params } from '@angular/router';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { AccountService } from 'src/app/services/account/account.service';
import { TranslateService } from '@ngx-translate/core';
import { UserAccounts } from 'src/app/interfaces/account';
import { PopupService } from 'src/app/services/general/popup.service';
import { PaymentResultType } from 'src/app/types/transaction';
import { Platform } from '@ionic/angular';
import { Credit } from 'src/app/interfaces/credit';

import { GAItem, GAPurchase } from 'src/app/interfaces/analytics';
import { Item } from 'src/app/interfaces/transaction';
import { BackService } from 'src/app/services/general/back.service';

@Component({
  selector: 'app-payment-result',
  templateUrl: './payment-result.page.html',
  styleUrls: ['./payment-result.page.scss'],
})
export class PaymentResultPage implements OnInit, OnDestroy {

  paymentUid: string;
  result: PaymentResultType;
  creditId: string;
  checkout: boolean;

  /**
   * Credit ID list
   */
  disableUpgrade: boolean;
  disableCreditPrompt: boolean;
  /**
   * Upgrade mode
   */
  upgradeMode: boolean;
  /**
   * Trial mode
   */
  trialMode: boolean;

  /**
   * Account ID
   */
  accountId: string;

  private debugMode: boolean;

  private iap: boolean;
  
  /**
   * Constructor
   * @param route route
   * @param router router
   * @param accountService account service
   */
  constructor(
    private platform: Platform,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private creditService: CreditService,
    private transactionService: TransactionService,
    private accountService: AccountService,
    private userService: UserService,
    private loginService: LoginService,
    private linkService: LinkService,
    private backService: BackService,
    private analyticsService: AnalyticsService,
    private popupService: PopupService,
  ) { }

  /**
   * On init
   */
  ngOnInit() {
    this.watchRoute();
  }

  ngOnDestroy(): void {
  }

  ionViewWillEnter() {
  }

  ionViewWillLeave() {
  }

  /**
   * Watch route
   */
  watchRoute() {
    this.route.params.subscribe(async (param: Params) => {
      const route = this.router.getCurrentNavigation();
      const state = route?.extras?.state;

      if (state?.accountId) {
        this.accountId = state?.accountId;
      } else if (param?.accountId) {
        this.accountId = param?.accountId;
      }

      if (state?.creditId) {
        this.creditId = state?.creditId;
      } else if (param?.creditId) {
        this.creditId = param?.creditId;
      }

      if (state?.result) {
        this.result = state?.result;
      } else if (param?.result) {
        this.result = param?.result;
      }

      if (state?.uid) {
        this.paymentUid = state?.uid;
      } else if (param?.uid) {
        this.paymentUid = param?.uid;
      }

      if (state?.checkout) {
        this.checkout = true;
      } else if (param?.checkout) {
        this.checkout = true;
      } else {
        this.checkout = false;
      }

      if (state?.upgradeMode) {
        this.upgradeMode = true;
      } else if (param?.upgradeMode) {
        this.upgradeMode = true;
      } else {
        this.upgradeMode = false;
      }

      if (state?.upgradeTrial) {
        this.trialMode = true;
      } else if (param?.upgradeTrial) {
        this.trialMode = true;
      } else {
        this.trialMode = false;
      }

      if (state?.debugMode) {
        this.debugMode = true;
      } else if (param?.debugMode) {
        this.debugMode = true;
      } else {
        this.debugMode = false;
      }

      if (state?.disableUpgrade) {
        this.disableUpgrade = true;
      } else if (param?.disableUpgrade) {
        this.disableUpgrade = true;
      } else {
        this.disableUpgrade = false;
      }

      if (state?.disableCreditPrompt) {
        this.disableCreditPrompt = true;
      } else if (param?.disableCreditPrompt) {
        this.disableCreditPrompt = true;
      } else {
        this.disableCreditPrompt = false;
      }

      if (state?.iap) {
        this.iap = true;
      } else if (param?.iap) {
        this.iap = true;
      } else {
        this.iap = false;
      }

      this.validateAccess();
    });
  }

  validateAccess() {
    let result = true;
    if (this.result) {
      if ((this.result === 'redeem' || this.result === 'restore') && !this.creditId) {
        result = false;
      } else if (this.result === 'success') {
        if (this.checkout) {
          if (!this.creditId || !this.paymentUid) {
            result = false;
          } else {
            this.analyticsPurchase();
            if (this.paymentUid !== this.userService.uid) {
              // different user
              if (!this.userService.uid) {
                this.presentLoginModal(true);
              } else {
                this.promptError(this.translate.instant('PAYMENT.msg.invalid_user'));
              }
            }
          }
        } else {
          if (!this.creditId) {
            result = false;
          } else {
            this.analyticsPurchase();
          }
        }
      }
    }
    this.popupService.dismissLoading();
    if (!result) {
      // prompt invalid access then go main page
      this.promptError(this.translate.instant('PAYMENT.msg.unknown'));
    }
  }

  async promptError(msg: string) {
    if (msg) {
      const alert = await this.popupService.presentAlert(msg);
      alert.onDidDismiss().then(() => {
        this.back();
      });
    }
  }

  /**
   * Present login modal
   * @param loginRequired login required
   * @param showAnonymousLogin show anonymous login
   * @param linkProvider link provider
   */
  async presentLoginModal(loginRequired?: boolean, showAnonymousLogin?: boolean, linkProvider?: boolean) {
    await this.popupService.dismissLoading();
    const modal = await this.loginService.presentLoginModal(loginRequired, showAnonymousLogin, linkProvider);
    modal.onDidDismiss().then(async () => {
      if (this.userService.user) {
        if (this.userService.uid !== this.paymentUid) {
          this.promptError(this.translate.instant('PAYMENT.msg.invalid_user'))
        }
      } else {
        this.back();
      }
    });
  }

  /**
   * Setup account
   */
  async setupAccount() {
    if (this.accountId) {
      if (await this.accountService.readAccountId()) {
        if (this.accountService.accountId !== this.accountId) {
          await this.accountService.logoutAccount();
          this.accountService.setupAccount(this.accountId);
        } else {
          this.goAccountHomePage();
        }
      } else {
        this.accountService.setupAccount(this.accountId);
      }
    }
  }

  /**
   * Prompt upgrade trial account
   * @param creditId Credit ID
   * @param userAccounts User account
   */
  async promptRedeemTrialAccount(userAccounts: UserAccounts, creditId: string) {
    if (creditId && userAccounts?.accountId) {
      const modal = await this.popupService.presentConfirm(
        this.translate.instant('ACCOUNT.msg.confirm_redeem_trial', { title: userAccounts?.title?.value })
      );
      modal.onWillDismiss().then((result: any) => {
        if (result?.data?.confirm) {
          const credit = this.creditService.getCreditById(creditId);
          if (credit?.creditId && credit?.enable && !credit?.redeemed) {
            this.accountService.upgradeAccountCall(credit, userAccounts.accountId, true);
          } else {
            this.popupService.presentActionError();
          }
        }
      });
    } else {
      this.popupService.presentActionError();
    }
  }

  back() {
    if (this.result === 'restore') {
      this.goInAppPurchasePage();
    } else if (this.result === 'redeem') {
      this.backService.goBack();
    } else if (this.result === 'fail' || this.result === 'success') {
      this.goProductPage();
    } else {
      this.goAccountsListPage();
    }
  }

  generateParam() {
    const navigationExtras: NavigationExtras = {
      state: {
        accountId: this.accountId,
        upgradeTrial: this.trialMode,
        upgradeMode: this.upgradeMode,
        disableUpgrade: this.disableUpgrade,
        disableCreditPrompt: this.disableCreditPrompt,
        backPremiumPage: true,
      }
    };
    return navigationExtras;
  }

  goInAppPurchasePage() {
    this.router.navigate(['/subscription/premium'], this.generateParam());
  }

  goProductAccountPage() {
    this.router.navigate(['/subscription/account'], this.generateParam());
  }


  /**
   * Go accounts list page
   */
  goAccountsListPage() {
    this.router.navigate(['/tabs-accounts/list']);
  }

  /**
   * Go account home page
   */
  goAccountHomePage() {
    this.router.navigate(['/account/home']);
  }

  goMainPage() {
    this.linkService.goMainPage();
  }

  goProductPage() {
    if (this.iap) {
      this.goInAppPurchasePage();
    } else {
      this.goProductAccountPage();
    }
  }

  getTransaction() {
    let transaction: Transaction;
    if (this.checkout && this.creditId) {
      const credit: Credit = this.creditService.getCreditById(this.creditId);
      if (credit?.creditId && credit?.transactionId) {
        transaction = this.transactionService.getTransactionById(credit.transactionId);
      }
    }
    return transaction;
  }

  async analyticsPurchase() {
    if (!this.debugMode) {
      await this.platform.ready();
      const transaction: Transaction = this.getTransaction();
      if (transaction?.transactionId && transaction?.itemList?.length) {
        const items: GAItem[] = [];
        transaction.itemList?.forEach((item: Item) => {
          if (item?.productId) {
            items.push({
              item_id: item.productId,
              item_category: 'account',
              item_name: item?.productName,
              quantity: item?.qty,
              price: item?.payment?.amount,
              currency: item?.payment?.currency,
            });
          }
        });
        const gaPurchase: GAPurchase = {
          currency: transaction?.payment?.currency,
          value: transaction?.payment?.amount,
          transaction_id: transaction.transactionId,
          coupon: transaction?.discount?.voucher?.id,
          tax: transaction?.taxRate,
          items
        };
        this.analyticsService.purchase(gaPurchase);
      }
    }
  }

}
